<template>
	<div class="Multiselect">
		<Multiselect
			ref="multiselectRef"
			v-model="internalValue"
			:mode="props.mode"
			:options="formattedOptions"
			:placeholder="props.placeholder"
			:disabled="props.disabled"
			:searchable="props.searchable"
			:close-on-select="props.mode === MultiselectMode.SINGLE"
			:can-clear="props.canClear"
			:hide-selected="props.mode === MultiselectMode.SINGLE"
			:caret="true"
			:no-options-text="props.noOptionsText"
			:no-results-text="props.noResultsText"
			class="MultiselectDropdown"
			@open="onOpen"
			@close="onClose"
		>
			<template #option="{ option, isSelected }">
				<div class="SelectItemWrapper">
					<SelectItem
						:item="mapOptionToSelectItem(option, isSelected(option))"
						:type="selectItemType"
					>
						<template v-if="option.icon" #icon>
							<i :class="`Icon ${option.icon}`" aria-hidden="true"></i>
						</template>
					</SelectItem>
				</div>
			</template>

			<template #singlelabel="{ value }">
				<div class="MultiselectSingleLabel">
					<i v-if="value.icon" :class="`Icon ${value.icon}`" aria-hidden="true"></i>
					<span>{{ value.label }}</span>
				</div>
			</template>

			<template v-if="props.mode === MultiselectMode.TAGS" #tag="{ option, handleTagRemove }">
				<Chip
					:key="option.value"
					:label="option.label"
					:count="option.count"
					@remove="(e: Event) => handleTagRemove(option, e)"
				/>
			</template>

			<template #caret>
				<i
					class="CaretIcon"
					:class="isOpen ? 'lnr-chevron-up' : 'lnr-chevron-down'"
					aria-hidden="true"
					@mousedown.prevent.stop="toggleDropdown"
				></i>
			</template>

			<template #clear="{ clear }">
				<i
					class="ClearIcon lnr-cross"
					aria-hidden="true"
					@mousedown.prevent.stop="clear"
				></i>
			</template>
		</Multiselect>
		<p v-if="props.description" class="MultiselectDescription">{{ props.description }}</p>
		<p v-if="props.warning" class="MultiselectWarning">{{ props.warning }}</p>
	</div>
</template>

<script setup lang="ts">
import { defineAsyncComponent, ref } from 'vue';
const Multiselect = defineAsyncComponent(() => import('@vueform/multiselect'));
import SelectItem from '@components/SelectItem/SelectItem.vue';
import Chip from '@components/TagsElement/Chip.vue';
import { useMultiselect } from '@composables/useMultiselect';
import { multiselectPropsDefinition } from '@app/interfaces/useMultiselect.interface';
import { MultiselectMode } from '@app/enums/MultiselectMode';

const props = defineProps(multiselectPropsDefinition);

const emit = defineEmits<{
	'updateValues': [value: string | number | (string | number)[] | null];
	'update:modelValue': [value: string | number | (string | number)[] | null];
	'open': [];
	'close': [];
}>();

const multiselectRef = ref<InstanceType<typeof Multiselect> | null>(null);

const {
	internalValue,
	isOpen,
	formattedOptions,
	selectItemType,
	mapOptionToSelectItem,
	onOpen,
	onClose
} = useMultiselect({ props, emit });

const toggleDropdown = () => {
	const multiselect = multiselectRef.value as any;
	if (multiselect?.isOpen) {
		multiselect?.close();
		multiselect?.deactivate();
	} else {
		multiselect?.open();
		multiselect?.activate();
	}
};
</script>

<style scoped lang="scss">
.Multiselect {
	width: 100%;

	.MultiselectDropdown {
		--ms-font-size: 1rem;
		--ms-font-weight: 400;
		--ms-bg: #{$White};
		--ms-border-color: #{$Grey-600};
		--ms-border-width: 1px;
		--ms-radius: 0.5rem;
		--ms-ring-width: 0;
		--ms-ring-color: transparent;
		--ms-placeholder-color: #{$Grey-600};
		--ms-caret-color: #{$Grey-600};
		--ms-clear-color: #{$Grey-600};
		--ms-clear-color-hover: #{$Grey-900};
		--ms-tag-my: 0;

		// Dropdown
		--ms-max-height: v-bind('props.maxHeight');
		--ms-dropdown-bg: #{$White};
		--ms-dropdown-border-color: #{$Grey-600};
		--ms-dropdown-border-width: 1px;
		--ms-dropdown-radius: 0.5rem;

		// Options
		--ms-option-font-size: 1rem;
		--ms-option-line-height: 1rem;
		--ms-option-bg-pointed: transparent;
		--ms-option-color-pointed: #{$Grey-900};
		--ms-option-bg-selected: transparent;
		--ms-option-bg-selected-pointed: transparent;
		--ms-option-color-selected: #{$Grey-900};
		--ms-option-color-selected-pointed: #{$Grey-900};
		--ms-option-py: 0.25rem;
		--ms-option-px: 0.5rem;

		@include PaddingAreas(0, 0.5);

		&:hover:not(.is-disabled):not(.is-active) {
			--ms-bg: #{$Blue-100};

			:deep(.multiselect-tags-search) {
				background: $Blue-100;
			}
		}
		&.is-active {
			outline: 2px solid #{$Blue-600};
		}
	}

	.MultiselectSingleLabel {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		position: absolute;
		left: 1rem;
		top: 0;
		width: 100%;
		height: 100%;
		pointer-events: none;

		.Icon {
			width: 1rem;
			height: 1rem;
			flex-shrink: 0;
		}
	}

	.CaretIcon {
		color: $Grey-600;
		z-index: $FirstFloor;
		cursor: pointer;
		@include MarginAreas(0, 0.5, 0, 0);
	}

	.ClearIcon {
		color: $Grey-600;
		cursor: pointer;
		@include MarginAreas(0, 0.5, 0, 0);

		&:hover {
			color: $Grey-900;
		}
	}

	:deep(.multiselect-tags-search-wrapper) {
		width: 100%;
	}

	:deep(.multiselect-tags) {
		gap: 0.5rem;

		div:last-child:not(:only-child) {
			@include PaddingAreas(0.5, 0, 0, 0);
		}
	}

	:deep(.multiselect-wrapper) {
		font-size: 1rem;
		line-height: 1rem;
		font-weight: 400;
		align-items: end;
		@include PaddingAreas(1, 0);
	}

	:deep(.multiselect-dropdown) {
		scrollbar-width: none;
		-ms-overflow-style: none;

		&::-webkit-scrollbar {
			display: none;
		}
	}

	:deep(.multiselect-tags) {
		@include PaddingAreas(0);
	}

	:deep(.multiselect-placeholder) {
		@include PaddingAreas(0.25);
	}

	.SelectItemWrapper {
		width: 100%;
		pointer-events: none;

		:deep(.SelectItem) {
			max-width: 100%;
			@include PaddingAreas(0.5);
		}
	}

	:deep(.multiselect-option) {
		@include PaddingAreas(0);

		&:hover .SelectItemWrapper .SelectItem {
			background: $Blue-100;
		}
	}

	.ChipsList {
		display: flex;
		flex-wrap: wrap;
		gap: 0.5rem;
		@include PaddingAreas(0.25, 0);
	}

	.MultiselectDescription {
		color: $Grey-700;
		@include TextStyle(BodyMediumRegular);
		@include MarginAreas(0.5, 0, 0, 0);
	}

	.MultiselectWarning {
		color: $Red-500;
		@include TextStyle(BodyMediumRegular);
		@include MarginAreas(0.5, 0, 0, 0);
	}
}
</style>
